/**
 * Pagina pubblica di conferma prolungamento soggiorno.
 * Il responsabile accede via link nella email e conferma quali ospiti sono ancora presenti.
 * Accessibile senza login tramite token univoco.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, Building, Calendar, Users, Loader2, Shield, AlertTriangle } from 'lucide-react';
import { API } from '../utils/config';

const ProlongationConfirmPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedGuests, setSelectedGuests] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/api/questura-prolongation/confirm/${token}`);
      if (res.ok) {
        const d = await res.json();
        setData(d);
        // Default: all selected
        const sel = {};
        (d.guests || []).forEach(g => { sel[g.document_id] = true; });
        setSelectedGuests(sel);
      } else {
        const err = await res.json();
        setError(err.detail || 'Richiesta non trovata o scaduta');
      }
    } catch (e) {
      setError('Errore di connessione');
    }
    setLoading(false);
  };

  const toggleGuest = (docId) => {
    setSelectedGuests(prev => ({ ...prev, [docId]: !prev[docId] }));
  };

  const selectAll = () => {
    const sel = {};
    (data?.guests || []).forEach(g => { sel[g.document_id] = true; });
    setSelectedGuests(sel);
  };

  const handleSubmit = async () => {
    const confirmedIds = Object.entries(selectedGuests)
      .filter(([_, v]) => v)
      .map(([id]) => id);

    if (confirmedIds.length === 0) {
      alert('Seleziona almeno un ospite presente');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/questura-prolongation/confirm/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmed_guest_ids: confirmedIds })
      });
      if (res.ok) {
        const r = await res.json();
        setResult(r);
        setSubmitted(true);
      } else {
        const err = await res.json();
        alert(err.detail || 'Errore conferma');
      }
    } catch (e) {
      alert('Errore di connessione');
    }
    setSubmitting(false);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const selectedCount = Object.values(selectedGuests).filter(Boolean).length;
  const totalCount = data?.guests?.length || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-gray-800 mb-2">Richiesta Non Valida</h2>
            <p className="text-gray-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (data?.status === 'sent' || data?.status === 'confirmed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-gray-800 mb-2">Conferma Gia' Effettuata</h2>
            <p className="text-gray-500">I documenti sono stati {data.status === 'sent' ? 'inviati alla Questura' : 'confermati'}.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted && result) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Conferma Ricevuta!</h2>
            <div className="space-y-2 mt-4 text-left bg-emerald-50 p-4 rounded-lg">
              <p className="text-sm"><strong>Ospiti confermati:</strong> {result.confirmed_guests} su {result.total_guests}</p>
              <p className="text-sm"><strong>Documenti copiati:</strong> {result.documents_copied}</p>
              <p className="text-sm">
                <strong>Invio Questura:</strong>{' '}
                {result.questura_sent ? (
                  <Badge className="bg-emerald-100 text-emerald-800">Inviati</Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800">In attesa</Badge>
                )}
              </p>
            </div>
            {result.questura_sent && (
              <p className="text-sm text-emerald-600 mt-4">
                I documenti sono stati inviati automaticamente alla Questura.
              </p>
            )}
            <p className="text-xs text-gray-400 mt-6">Puoi chiudere questa pagina.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="prolongation-confirm-page">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <Users className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Conferma Ospiti Presenti</h1>
          <p className="text-gray-500 mt-1">Prolungamento soggiorno</p>
        </div>

        {/* Booking Info */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start gap-3 mb-4">
              <Building className="h-5 w-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="font-bold text-gray-800">{data.property_code} - {data.property_name}</p>
                <p className="text-sm text-gray-500">Ospite: {data.guest_name}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Soggiorno originale</p>
                <p className="text-sm font-medium flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatDate(data.original_check_in)} - {formatDate(data.original_check_out)}
                </p>
              </div>
              <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                <p className="text-xs text-emerald-600 mb-1">Prolungamento</p>
                <p className="text-sm font-medium text-emerald-800 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatDate(data.check_in)} - {formatDate(data.check_out)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Guest Selection */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Chi e' ancora presente?</CardTitle>
              <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
                Seleziona tutti
              </Button>
            </div>
            <p className="text-sm text-gray-500">Deseleziona gli ospiti che sono partiti</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data.guests || []).map((guest) => {
              const name = `${guest.first_name || ''} ${guest.last_name || ''}`.trim();
              const isSelected = selectedGuests[guest.document_id];
              return (
                <div
                  key={guest.document_id}
                  onClick={() => toggleGuest(guest.document_id)}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 bg-white opacity-60'
                  }`}
                  data-testid={`guest-toggle-${guest.document_id}`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                    isSelected ? 'bg-emerald-500' : 'bg-gray-200'
                  }`}>
                    {isSelected ? (
                      <CheckCircle className="h-4 w-4 text-white" />
                    ) : (
                      <XCircle className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${isSelected ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                      {name || 'Ospite'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {guest.document_type} {guest.document_number && `- ${guest.document_number}`}
                    </p>
                  </div>
                  <Badge className={isSelected ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}>
                    {isSelected ? 'Presente' : 'Partito'}
                  </Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Warning if not all selected */}
        {selectedCount < totalCount && selectedCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg flex gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {totalCount - selectedCount} ospite/i deselezionato/i
              </p>
              <p className="text-xs text-amber-600 mt-1">
                I documenti degli ospiti deselezionati NON verranno inviati alla Questura per il prolungamento.
              </p>
            </div>
          </div>
        )}

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={submitting || selectedCount === 0}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg"
          data-testid="confirm-submit-btn"
        >
          {submitting ? (
            <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Invio in corso...</>
          ) : (
            <><Shield className="h-5 w-5 mr-2" /> Conferma {selectedCount} ospite/i presente/i</>
          )}
        </Button>

        <p className="text-center text-xs text-gray-400">
          Dopo la conferma il sistema inviera' automaticamente i documenti alla Questura.
        </p>
      </div>
    </div>
  );
};

export default ProlongationConfirmPage;
